import React, {useEffect, useRef, useState} from "react";

interface WidgetDataProps {
  datas: any[];
  openPopup: (row?: any) => void;
  handleDelete: (row?: any) => void;
}

const WidgetData: React.FC<WidgetDataProps> = ({
  datas,
  openPopup,
  handleDelete
}) => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const buttonRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        openIndex !== null &&
        buttonRefs.current[openIndex] &&
        !buttonRefs.current[openIndex]?.contains(event.target as Node)
      ) {
        setOpenIndex(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [openIndex]);

  const alldatas = datas.filter((data: any) =>
    data.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getPagination = ({
    totalItems,
    currentPage,
    pageSize,
    siblingCount = 1
  }: {
    totalItems: number;
    currentPage: number;
    pageSize: number;
    siblingCount?: number;
  }) => {
    const totalPages = Math.ceil(totalItems / pageSize);
    if (totalPages <= 1) return [];

    const totalPageNumbers = siblingCount * 2 + 5;
    if (totalPageNumbers >= totalPages) {
      return Array.from({length: totalPages}, (_, i) => i + 1);
    }

    const leftSibling = Math.max(currentPage - siblingCount, 1);
    const rightSibling = Math.min(currentPage + siblingCount, totalPages);
    const showLeftDots = leftSibling > 2;
    const showRightDots = rightSibling < totalPages - 1;

    const range: (number | string)[] = [];

    if (!showLeftDots && showRightDots) {
      for (let i = 1; i <= 3 + 2 * siblingCount; i++) range.push(i);
      range.push("...", totalPages);
    } else if (showLeftDots && !showRightDots) {
      range.push(1, "...");
      for (
        let i = totalPages - (3 + 2 * siblingCount) + 1;
        i <= totalPages;
        i++
      )
        range.push(i);
    } else {
      range.push(1, "...");
      for (let i = leftSibling; i <= rightSibling; i++) range.push(i);
      range.push("...", totalPages);
    }

    return range;
  };

  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 3;
  const totalItems = alldatas.length;
  const totalPages = Math.ceil(totalItems / pageSize);
  const start = (currentPage - 1) * pageSize;
  const end = start + pageSize;
  const overalldatas = alldatas.slice(start, end);
  const pagination = getPagination({totalItems, currentPage, pageSize});

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };

  const handlePageClick = (page: number | string) => {
    if (typeof page === "number" && page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  return (
    <>
      <div className="mb-2">
        <input
          type="text"
          placeholder="Search..."
          value={searchQuery}
          onChange={handleSearch}
          className="w-full rounded border px-2 py-1"
        />
      </div>
      <div className="space-y-2">
        {overalldatas.map((row, index) => (
          <div
            key={index}
            className="flex items-center justify-between rounded-lg border bg-gray-50 p-3"
          >
            <div>
              <p className="text-sm font-medium">{row.name}</p>
              <p className="text-xs text-gray-500">{row.address}</p>
            </div>
            <div
              ref={el => {
                buttonRefs.current[index] = el;
              }}
              className="relative"
            >
              <button
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
                className="flex h-8 w-8 items-center justify-center rounded hover:bg-gray-100"
              >
                <span className="text-xl leading-none text-gray-400">⋮</span>
              </button>
              {openIndex === index && (
                <div className="absolute left-5 z-50 min-w-32 -translate-x-full rounded-xl border bg-white p-1 shadow-lg">
                  <button
                    onClick={() => {
                      openPopup(row);
                      setOpenIndex(null);
                    }}
                    className="w-full whitespace-nowrap rounded-md p-2 text-left text-sm text-gray-700 hover:bg-[#F5A524]"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => {
                      handleDelete(row);
                      setOpenIndex(null);
                    }}
                    className="w-full whitespace-nowrap rounded-md p-2 text-left text-sm text-gray-700 hover:bg-[#F5A524]"
                  >
                    Delete
                  </button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
      <div className="mt-4 flex items-center justify-between text-xs text-gray-500">
        <p>
          Showing {start + 1}–{Math.min(end, totalItems)} of {totalItems}{" "}
          Records
        </p>
        <div className="flex items-center gap-1">
          <button
            onClick={() => handlePageClick(currentPage - 1)}
            disabled={currentPage === 1}
            className="px-2 py-1 text-gray-500 hover:text-black disabled:opacity-30"
          >
            &lt;
          </button>
          {pagination.map((page, idx) =>
            page === "..." ? (
              <span key={idx} className="px-2">
                ...
              </span>
            ) : (
              <button
                key={page}
                onClick={() => handlePageClick(page)}
                className={`rounded px-2 py-1 ${
                  page === currentPage
                    ? "bg-blue-100 text-blue-600"
                    : "hover:bg-gray-100"
                }`}
              >
                {page}
              </button>
            )
          )}
          <button
            onClick={() => handlePageClick(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="px-2 py-1 text-gray-500 hover:text-black disabled:opacity-30"
          >
            &gt;
          </button>
        </div>
      </div>
    </>
  );
};

export default WidgetData;
